import { useCallback, useEffect, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useNavigate } from 'react-router-dom';
import { rovingTabIndex, useRovingRadioGroup } from '../components/rovingFocus';
import {
  ArrowDownTrayIcon,
  ArrowRightStartOnRectangleIcon,
  BugAntIcon,
  CheckIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  MoonIcon,
  ServerStackIcon,
  ShareIcon,
  ShieldCheckIcon,
  SunIcon,
  SwatchIcon,
  UserCircleIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Locale, Theme, UserRole } from '@xphoto/shared';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { useTheme } from '../theme';
import { useInstallPrompt } from '../pwa';
import { useHealth } from '../lib/useHealth';
import { interpolate } from '../gallery/format';
import { DEFAULT_APP_NAME } from '../app-config';
import { Portal } from '../components/Portal';
import { usePopoverClamp } from '../components/usePopoverClamp';
import { BugReportModal } from '../diagnostics/BugReportModal';
import { useAuth } from './AuthProvider';

/** Clé i18n du libellé de rôle. */
const ROLE_LABEL: Record<UserRole, MessageKey> = {
  superadmin: 'auth.roleSuperadmin',
  admin: 'auth.roleAdmin',
  user: 'auth.roleUser',
};

/** Clé i18n du libellé de thème (aria-label du bouton radio). */
const THEME_LABEL: Record<Theme, MessageKey> = {
  light: 'theme.light',
  dark: 'theme.dark',
  system: 'theme.system',
};

/** Icône du contrôle segmenté de thème. */
const THEME_ICON: Record<Theme, React.ReactNode> = {
  light: <SunIcon className="h-5 w-5" aria-hidden="true" />,
  dark: <MoonIcon className="h-5 w-5" aria-hidden="true" />,
  system: <ComputerDesktopIcon className="h-5 w-5" aria-hidden="true" />,
};

/**
 * Version affichée dans le footer : `version+build` (sha git) pour aider au debug
 * et distinguer deux builds au même SemVer. Le `+build` est masqué en dev (buildId
 * absent ou `'dev'`) — même convention que le préambule « Signaler un bug ».
 */
function versionLabel(version: string, buildId: string): string {
  return buildId && buildId !== 'dev' ? `${version}+${buildId}` : version;
}

/** Style commun d'une entrée d'action (bouton pleine largeur). */
const ROW =
  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none';

/**
 * Menu de compte (multi-login T7c) déclenché par l'engrenage de l'en-tête.
 *
 * Panneau déroulant (popover étiqueté, PAS `role="menu"` — il contient un
 * radiogroup de thème et un select de langue, incompatibles avec `menu`) :
 * comptes empilés (actif marqué par ✓ + `aria-checked`, jamais la couleur seule),
 * bascule sans re-login, ajout de compte, contrôle Thème/Langue inline, navigation
 * vers les PAGES Mon compte (Paramètres) / Admin (sous l'en-tête persistant),
 * modale « Signaler un bug », « Se déconnecter » (tous les comptes, confirmation en
 * deux temps) et footer version via `/api/health`.
 * Accessible : Échap ferme (focus rendu au déclencheur), clic extérieur ferme.
 *
 * Ses DEUX `radiogroup` (comptes empilés, thème) ont flèches + `tabIndex` mobile
 * (`rovingFocus.ts`), avec des activations DIFFÉRENTES et assumées :
 *  - COMPTES en MANUEL — choisir un compte appelle `switchTo` (bascule de session) ET
 *    referme le panneau : suivre le focus changerait de compte à chaque flèche ;
 *  - THÈME en AUTOMATIQUE (motif ARIA standard) — c'est une préférence locale sans
 *    appel réseau, dont l'aperçu immédiat en parcourant les options est justement
 *    l'intérêt.
 */
export function AccountSwitcher(): React.JSX.Element | null {
  const { t, locale, setLocale, locales } = useI18n();
  const { theme, setTheme, themes } = useTheme();
  const { user, accounts, activeSlot, switchTo, logout, addAccount } = useAuth();
  const { health } = useHealth();
  const { status: installStatus, promptInstall } = useInstallPrompt();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const onAccountsKeyDown = useRovingRadioGroup('manual');
  const onThemeKeyDown = useRovingRadioGroup('auto');
  const [reportOpen, setReportOpen] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Maintien dans le cadre visible : `min-w-72` (288 px) est le plus large panneau du
  // projet — il ne tient dans 360 px qu'à condition d'être ramené dans le cadre.
  const panelRef = usePopoverClamp(open);
  /**
   * Déconnexion en vol (audit F9) : garde de double-soumission. Un `ref` (posé
   * SYNCHRONEMENT, avant tout rendu) + un état pour le `disabled` du bouton — deux
   * clics rapides déclenchaient sinon deux `POST /logout` concurrents, dont le second
   * courait contre le `refreshAccounts` du premier.
   */
  const loggingOutRef = useRef(false);
  const [loggingOut, setLoggingOut] = useState(false);

  /** Déconnecte un slot (ou tous), une seule fois à la fois. */
  const runLogout = useCallback(
    (slot: number, all = false): void => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      setLoggingOut(true);
      void logout(slot, all).finally(() => {
        loggingOutRef.current = false;
        setLoggingOut(false);
      });
    },
    [logout],
  );

  const isSuperadmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin';
  // Admin de GALERIE : superadmin, admin (écriture globale par son rôle) OU
  // propriétaire d'une bibliothèque. Aligné sur la garde de route `AdminRoute`.
  const canAdmin = isSuperadmin || isAdmin || (user?.libraryId ?? null) !== null;

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    setConfirmingAll(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    // Échap ferme quelle que soit la position du focus (déclencheur inclus).
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!user) return null;

  const onLogoutAll = (): void => {
    if (!confirmingAll) {
      setConfirmingAll(true);
      return;
    }
    runLogout(activeSlot ?? accounts[0]?.slot ?? 0, true);
    close(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('auth.account')}
        title={t('auth.account')}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
          open ? 'bg-surface-2 text-fg outline-2 outline-offset-2 outline-brand' : ''
        }`}
      >
        <Cog6ToothIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={panelRef}
          aria-label={t('accountSwitcher.title')}
          className="absolute end-0 z-20 mt-1 min-w-72 rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {/* 1. Titre */}
          <p id="account-switcher-title" className="px-3 py-2 text-xs font-medium text-muted">
            {t('accountSwitcher.title')}
          </p>

          {/* 2. Comptes empilés (le compte actif = radio coché, jamais la couleur seule) */}
          <div
            role="radiogroup"
            aria-labelledby="account-switcher-title"
            onKeyDown={onAccountsKeyDown}
          >
            {accounts.map((account) => {
              const isActive = account.slot === activeSlot;
              return (
                <div key={account.slot} className="flex items-stretch gap-1">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    tabIndex={rovingTabIndex(isActive)}
                    title={account.email}
                    onClick={() => {
                      if (!isActive) switchTo(account.slot);
                      close(false);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
                  >
                    <UserCircleIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {account.displayName}
                    </span>
                    <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted">
                      {t(ROLE_LABEL[account.role])}
                    </span>
                    {isActive ? (
                      <CheckIcon
                        className="h-5 w-5 shrink-0 text-brand"
                        aria-label={t('accountSwitcher.active')}
                      />
                    ) : (
                      <span className="h-5 w-5 shrink-0" aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={`${t('accountSwitcher.logoutAccount')} — ${account.email}`}
                    title={t('accountSwitcher.logoutAccount')}
                    onClick={() => runLogout(account.slot)}
                    disabled={loggingOut}
                    className="flex w-9 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-danger focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand disabled:opacity-50 motion-reduce:transition-none"
                  >
                    <ArrowRightStartOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* 3. Ajouter un compte */}
          <button
            type="button"
            onClick={() => {
              addAccount();
              close(false);
            }}
            className={`${ROW} text-fg`}
          >
            <UserPlusIcon className="h-5 w-5" aria-hidden="true" />
            <span>{t('accountSwitcher.addAccount')}</span>
          </button>

          {/* 4. Séparateur */}
          <div className="my-1 border-t border-border" />

          {/* 5. Thème (contrôle segmenté) */}
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-fg">
            <SwatchIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
            <span className="flex-1">{t('accountSwitcher.theme')}</span>
            <div
              role="radiogroup"
              aria-label={t('accountSwitcher.theme')}
              onKeyDown={onThemeKeyDown}
              className="flex items-center gap-1 rounded-md border border-border p-0.5"
            >
              {themes.map((th) => {
                const selected = th === theme;
                return (
                  <button
                    key={th}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    tabIndex={rovingTabIndex(selected)}
                    aria-label={t(THEME_LABEL[th])}
                    title={t(THEME_LABEL[th])}
                    onClick={() => setTheme(th as Theme)}
                    className={`flex h-8 w-8 items-center justify-center rounded transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
                      selected ? 'bg-brand text-brand-fg' : 'text-muted hover:bg-surface-2'
                    }`}
                  >
                    {THEME_ICON[th]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 6. Langue (select natif) */}
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-fg">
            <GlobeAltIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
            <span className="flex-1">{t('accountSwitcher.language')}</span>
            <select
              aria-label={t('accountSwitcher.language')}
              value={locale}
              onChange={(e) => setLocale(e.target.value as Locale)}
              className="h-9 rounded-md border border-border-strong bg-surface px-2 text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
            >
              {locales.map((l) => (
                <option key={l} value={l}>
                  {t(`lang.${l}`)}
                </option>
              ))}
            </select>
          </div>

          {/* 7. Séparateur */}
          <div className="my-1 border-t border-border" />

          {/* 8. Mon compte (route `/parametres/compte` — section compte des Paramètres) */}
          <button
            type="button"
            onClick={() => {
              navigate('/parametres/compte');
              close(false);
            }}
            className={`${ROW} text-fg`}
          >
            <UserCircleIcon className="h-5 w-5" aria-hidden="true" />
            <span>{t('accountSwitcher.myAccount')}</span>
          </button>

          {/* 9. Admin de GALERIE (`/admin/acces`) : Accès / Erreurs / Poubelle.
              Superadmin, admin (écriture par son rôle) OU propriétaire d'une
              bibliothèque. La Maintenance (réinitialisation) a migré vers la console
              Superadmin/Système (`/superadmin/reinitialiser`). */}
          {canAdmin && (
            <button
              type="button"
              onClick={() => {
                navigate('/admin/acces');
                close(false);
              }}
              className={`${ROW} text-fg`}
            >
              <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
              <span>{t('admin.open')}</span>
            </button>
          )}

          {/* 9 bis. Superadmin / SYSTÈME (`/superadmin/comptes`) : Comptes / Journaux /
              Configuration LLM / Journal des appels IA. Superadmin UNIQUEMENT. */}
          {isSuperadmin && (
            <button
              type="button"
              onClick={() => {
                navigate('/superadmin/comptes');
                close(false);
              }}
              className={`${ROW} text-fg`}
            >
              <ServerStackIcon className="h-5 w-5" aria-hidden="true" />
              <span>{t('superadmin.open')}</span>
            </button>
          )}

          {/* 10. Installer l'application (affichée seulement si installable ou iOS
              manuel — masquée si déjà installée ou navigateur non éligible). */}
          {(installStatus === 'installable' || installStatus === 'ios-manual') && (
            <button
              type="button"
              onClick={() => {
                if (installStatus === 'ios-manual') {
                  setIosHelpOpen(true);
                  close(false);
                } else {
                  void promptInstall();
                  close(false);
                }
              }}
              className={`${ROW} text-fg`}
            >
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
              <span>{t('pwa.install')}</span>
            </button>
          )}

          {/* 11. Signaler un bug */}
          <button
            type="button"
            onClick={() => {
              setReportOpen(true);
              close(false);
            }}
            className={`${ROW} text-fg`}
          >
            <BugAntIcon className="h-5 w-5" aria-hidden="true" />
            <span>{t('bugReport.open')}</span>
          </button>

          {/* 12. Séparateur */}
          <div className="my-1 border-t border-border" />

          {/* 13. Se déconnecter (tous les comptes, confirmation en deux temps) */}
          <button type="button" onClick={onLogoutAll} className={`${ROW} text-danger`}>
            <ArrowRightStartOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
            <span>{confirmingAll ? t('accountSwitcher.confirmLogoutAll') : t('auth.signOut')}</span>
          </button>

          {/* 14. Footer version (avec le sha git : aide au debug + signale un
              nouveau build même sans bump SemVer). `+build` masqué en dev. */}
          {health && (
            <div className="border-t border-border px-3 py-2 text-xs text-muted">
              {`${health.name} ${versionLabel(health.version, health.buildId)} · ${health.publicHostname}`}
            </div>
          )}
        </div>
      )}
      {/* « Signaler un bug » reste une modale locale (non routée) portée vers
          <body> : l'en-tête porte `backdrop-blur` (bloc conteneur des `fixed`),
          sans quoi elle s'ouvrirait bornée à la frame du haut au lieu du plein
          écran. Paramètres/Admin sont désormais des ROUTES (voir `App`). */}
      {reportOpen && (
        <Portal>
          <BugReportModal onClose={() => setReportOpen(false)} />
        </Portal>
      )}
      {/* Instructions manuelles iOS (pas de `beforeinstallprompt` sur Safari iOS) —
          portée vers <body> comme les autres modales de l'en-tête. */}
      {iosHelpOpen && (
        <Portal>
          <IosInstallHelp onClose={() => setIosHelpOpen(false)} />
        </Portal>
      )}
    </div>
  );
}

/**
 * Petite modale d'instructions pour installer la PWA sur iOS Safari (qui n'expose
 * pas `beforeinstallprompt`). Accessible : `role="dialog"`, focus initial sur le
 * bouton de fermeture, Échap ferme, clic sur le fond ferme.
 */
function IosInstallHelp({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { t } = useI18n();
  // Nom du site (LOT 3) interpolé dans l'intro, via `/api/health` (public). Repli sur
  // le nom historique tant que la santé n'a pas répondu.
  const { health } = useHealth();
  const siteName = health?.name ?? DEFAULT_APP_NAME;
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <ModalOverlay onDismiss={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('pwa.iosTitle')}
        className="flex w-full max-w-sm flex-col gap-4 rounded-lg border border-border bg-surface p-5 text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-fg">{t('pwa.iosTitle')}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            title={t('pwa.iosClose')}
            aria-label={t('pwa.iosClose')}
            className="rounded-md p-1.5 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-muted">{interpolate(t('pwa.iosIntro'), { name: siteName })}</p>
        <ol className="flex flex-col gap-3 text-sm text-fg">
          <li className="flex items-center gap-3">
            <ShareIcon className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" />
            <span>{t('pwa.iosStep1')}</span>
          </li>
          <li className="flex items-center gap-3">
            <ArrowDownTrayIcon className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" />
            <span>{t('pwa.iosStep2')}</span>
          </li>
        </ol>
      </div>
    </ModalOverlay>
  );
}
